// Platform back-office — the operator (cross-tenant) console.
//
// Visible only to platform admins (user.is_admin, resolved server-side
// from the PLATFORM_ADMIN_EMAILS allowlist or a back-office grant) and
// guarded again at the route level in App.js. Everything here talks to
// the /api/admin/platform/* endpoints, which are require_platform_admin
// gated server-side — hiding the menu is UX, the API is the boundary.
//
// Capabilities:
//   • Overview counters: tenants, signups, blocked, traffic, conversions.
//   • Tenant directory with per-account plan, usage and last activity.
//   • Block / unblock any client account (cuts off all sessions at once).
//   • Grant / revoke the platform-admin flag (root admins from the env
//     allowlist are read-only here by design — changing them is a Railway
//     config change, the UI labels them accordingly).
//   • Registration state badge — self-signup is env-driven
//     (DISABLE_REGISTRATION on Railway), shown read-only so the operator
//     can confirm what production is actually running with.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import XpnsrPageHeader from './XpnsrPageHeader';
import ConfirmDialog from './ConfirmDialog';
import OpsPanel from './OpsPanel';
import TrialFunnel from './TrialFunnel';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Activity,
  Ban,
  CreditCard,
  MousePointerClick,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  Target,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';

const PER_PAGE = 25;

// Mirrors billing_plans.PLAN_ORDER on the backend (validated server-side,
// so a drift here can never write an unknown plan).
const PLAN_ORDER = ['start', 'pro', 'team', 'network', 'enterprise'];

function fmtNum(value) {
  if (value === null || value === undefined) return '—';
  try {
    return Number(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function fmtDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
}

function planLabel(plan) {
  if (!plan) return '—';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <Card data-testid={`platform-stat-${label}`}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        <div className="mt-1 text-2xl font-display">{value}</div>
        {hint ? <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

export default function PlatformAdmin() {
  const { t } = useT();
  const [overview, setOverview] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // { kind: 'block' | 'unblock' | 'suspendTraffic' | 'resumeTraffic' | 'grant' | 'revoke' | 'plan' | 'delete', user: row }
  const [pendingAction, setPendingAction] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [planChoice, setPlanChoice] = useState('network');
  const searchTimer = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/platform/overview`);
      setOverview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Failed to load platform overview'));
    }
  }, [t]);

  const loadIntegrity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/platform/integrity`);
      setIntegrity(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Failed to load integrity status'));
    }
  }, [t]);

  const loadUsers = useCallback(
    async (nextPage = 1, term = '') => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/platform/users`, {
          params: { page: nextPage, per_page: PER_PAGE, search: term || undefined },
        });
        setRows(res.data.items || []);
        setTotal(res.data.total || 0);
        setPage(res.data.page || nextPage);
      } catch (e) {
        toast.error(e.response?.data?.detail || t('Failed to load users'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadOverview();
    loadIntegrity();
    loadUsers(1, '');
  }, [loadOverview, loadIntegrity, loadUsers]);

  const onSearchChange = (value) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadUsers(1, value), 350);
  };

  const refresh = () => {
    loadOverview();
    loadIntegrity();
    loadUsers(page, search);
  };

  const applyPatch = async (userRow, body, successMessage) => {
    setBusyId(userRow.id);
    try {
      await axios.patch(`${API}/admin/platform/users/${userRow.id}`, body);
      toast.success(successMessage);
      await Promise.all([loadUsers(page, search), loadOverview()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setBusyId(null);
      setPendingAction(null);
      setBlockReason('');
    }
  };

  const deleteAccount = async (userRow) => {
    setBusyId(userRow.id);
    try {
      await axios.delete(`${API}/admin/platform/users/${userRow.id}`);
      toast.success(t('Account deleted'));
      await Promise.all([loadUsers(page, search), loadOverview()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setBusyId(null);
      setPendingAction(null);
    }
  };

  const confirmPending = () => {
    if (!pendingAction) return;
    const { kind, user } = pendingAction;
    if (kind === 'block') {
      applyPatch(
        user,
        { is_active: false, blocked_reason: blockReason.trim() || undefined },
        t('Account blocked')
      );
    } else if (kind === 'unblock') {
      applyPatch(user, { is_active: true }, t('Account unblocked'));
    } else if (kind === 'suspendTraffic') {
      applyPatch(user, { traffic_suspended: true }, t('Traffic suspended'));
    } else if (kind === 'resumeTraffic') {
      applyPatch(user, { traffic_suspended: false }, t('Traffic resumed'));
    } else if (kind === 'grant') {
      applyPatch(user, { platform_admin: true }, t('Platform admin granted'));
    } else if (kind === 'revoke') {
      applyPatch(user, { platform_admin: false }, t('Platform admin revoked'));
    } else if (kind === 'plan') {
      applyPatch(user, { plan: planChoice }, t('Plan updated'));
    } else if (kind === 'delete') {
      deleteAccount(user);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const registrationOpen = Boolean(overview?.registration_enabled);

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      <XpnsrPageHeader
        kicker={t('Operator')}
        title={t('Platform admin')}
        subtitle={t('Cross-tenant back-office: accounts, access and platform health.')}
        actions={
          <Button variant="outline" size="sm" onClick={refresh} data-testid="platform-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />
            {t('Refresh')}
          </Button>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Users}
          label={t('Accounts')}
          value={fmtNum(overview?.users_total)}
          hint={`${t('Blocked')}: ${fmtNum(overview?.users_blocked)}`}
        />
        <StatCard
          icon={UserPlus}
          label={t('New sign-ups')}
          value={fmtNum(overview?.users_new_7d)}
          hint={`${t('Last 30 days')}: ${fmtNum(overview?.users_new_30d)}`}
        />
        <StatCard
          icon={MousePointerClick}
          label={t('Clicks (24h)')}
          value={fmtNum(overview?.clicks_24h)}
          hint={`${t('Last 30 days')}: ${fmtNum(overview?.clicks_30d)}`}
        />
        <StatCard
          icon={Target}
          label={t('Conversions (30d)')}
          value={fmtNum(overview?.conversions_30d)}
          hint={`${t('Active campaigns')}: ${fmtNum(overview?.campaigns_active)}`}
        />
      </div>

      <TrialFunnel />

      <Card className={integrity?.ok === false ? 'border-destructive' : ''}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            {t('Data integrity')}
            <Badge variant={integrity?.ok ? 'default' : 'destructive'}>
              {integrity?.ok ? t('Healthy') : t('Attention required')}
            </Badge>
          </CardTitle>
          <CardDescription>
            {t('Mandatory unique indexes protect click attribution, conversions and billing idempotency.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {integrity?.ok ? (
            <p className="text-sm text-muted-foreground">{t('All mandatory indexes are present and unique.')}</p>
          ) : (
            <div className="text-sm text-destructive space-y-1">
              <p>{t('Do not ignore this warning. Review backend startup logs before changing indexes.')}</p>
              {(integrity?.missing || []).length > 0 ? (
                <p>{t('Missing')}: {(integrity.missing || []).join(', ')}</p>
              ) : null}
              {(integrity?.non_unique || []).length > 0 ? (
                <p>{t('Not unique')}: {(integrity.non_unique || []).join(', ')}</p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4" />
            {t('Platform state')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('Self-signup')}:</span>
            <Badge variant={registrationOpen ? 'default' : 'destructive'} data-testid="registration-state">
              {registrationOpen ? t('Open') : t('Closed')}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('Controlled by the DISABLE_REGISTRATION variable on Railway.')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('Root admins')}:</span>
            <span className="font-mono text-xs">
              {(overview?.root_admin_emails || []).join(', ') || '—'}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('From PLATFORM_ADMIN_EMAILS — manage the list on Railway.')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('Granted admins')}:</span>
            <span>{fmtNum(overview?.granted_admins)}</span>
          </div>
        </CardContent>
      </Card>

      <OpsPanel />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">{t('Accounts')}</CardTitle>
              <CardDescription>
                {t('Every registered account. Block access or grant platform admin.')}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('Search by email or name…')}
                className="pl-8"
                data-testid="platform-users-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="platform-users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('User')}</TableHead>
                  <TableHead>{t('Created')}</TableHead>
                  <TableHead>{t('Plan')}</TableHead>
                  <TableHead className="text-right">{t('Clicks (30d)')}</TableHead>
                  <TableHead className="text-right">{t('Campaigns')}</TableHead>
                  <TableHead>{t('Last click')}</TableHead>
                  <TableHead>{t('Role')}</TableHead>
                  <TableHead className="text-right">{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      {t('Loading…')}
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      {t('No accounts found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} data-testid={`platform-user-${row.email}`}>
                      <TableCell>
                        <div className="font-medium">{row.email}</div>
                        <div className="text-xs text-muted-foreground">{row.full_name}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span>{planLabel(row.plan)}</span>
                          {row.subscription_status ? (
                            <Badge
                              variant={
                                row.subscription_status === 'active'
                                  ? 'default'
                                  : row.subscription_status === 'trialing'
                                    ? 'secondary'
                                    : 'destructive'
                              }
                              className="text-[0.65rem]"
                            >
                              {row.subscription_status}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtNum(row.clicks_30d)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtNum(row.campaigns)}</TableCell>
                      <TableCell className="whitespace-nowrap">{fmtDate(row.last_click_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.root_admin ? (
                            <Badge variant="default" className="text-[0.65rem]">
                              {t('Root admin')}
                            </Badge>
                          ) : row.platform_admin ? (
                            <Badge variant="default" className="text-[0.65rem]">
                              {t('Admin')}
                            </Badge>
                          ) : null}
                          {!row.is_active ? (
                            <Badge variant="destructive" className="text-[0.65rem]">
                              {t('Blocked')}
                            </Badge>
                          ) : null}
                          {row.traffic_suspended ? (
                            <Badge variant="destructive" className="text-[0.65rem]">
                              {t('Traffic suspended')}
                            </Badge>
                          ) : null}
                          {row.root_admin || row.platform_admin || !row.is_active ? null : (
                            <span className="text-xs text-muted-foreground">{t('Client')}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyId === row.id}
                            onClick={() => {
                              setPlanChoice(row.plan && PLAN_ORDER.includes(row.plan) ? row.plan : 'network');
                              setPendingAction({ kind: 'plan', user: row });
                            }}
                            aria-label={t('Change plan')}
                            data-testid={`set-plan-${row.email}`}
                          >
                            <CreditCard className="w-3.5 h-3.5 mr-1" />
                            {t('Plan')}
                          </Button>
                          {row.root_admin ? (
                            <span className="text-xs text-muted-foreground self-center">
                              {t('Managed on Railway')}
                            </span>
                          ) : (
                            <>
                              {row.platform_admin ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={busyId === row.id}
                                  onClick={() => setPendingAction({ kind: 'revoke', user: row })}
                                  aria-label={t('Revoke platform admin')}
                                  data-testid={`revoke-admin-${row.email}`}
                                >
                                  <ShieldOff className="w-3.5 h-3.5 mr-1" />
                                  {t('Revoke admin')}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={busyId === row.id}
                                  onClick={() => setPendingAction({ kind: 'grant', user: row })}
                                  aria-label={t('Grant platform admin')}
                                  data-testid={`grant-admin-${row.email}`}
                                >
                                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                                  {t('Make admin')}
                                </Button>
                              )}
                              {row.traffic_suspended ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={busyId === row.id}
                                  onClick={() =>
                                    setPendingAction({ kind: 'resumeTraffic', user: row })
                                  }
                                  aria-label={t('Resume campaign traffic')}
                                  data-testid={`resume-traffic-${row.email}`}
                                >
                                  <PlayCircle className="w-3.5 h-3.5 mr-1" />
                                  {t('Resume traffic')}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={busyId === row.id || !row.is_active}
                                  onClick={() =>
                                    setPendingAction({ kind: 'suspendTraffic', user: row })
                                  }
                                  aria-label={t('Suspend campaign traffic')}
                                  data-testid={`suspend-traffic-${row.email}`}
                                >
                                  <PauseCircle className="w-3.5 h-3.5 mr-1" />
                                  {t('Suspend traffic')}
                                </Button>
                              )}
                              {row.is_active ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={busyId === row.id}
                                  onClick={() => {
                                    setBlockReason('');
                                    setPendingAction({ kind: 'block', user: row });
                                  }}
                                  aria-label={t('Block account')}
                                  data-testid={`block-user-${row.email}`}
                                >
                                  <Ban className="w-3.5 h-3.5 mr-1" />
                                  {t('Block')}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busyId === row.id}
                                    onClick={() => setPendingAction({ kind: 'unblock', user: row })}
                                    aria-label={t('Unblock account')}
                                    data-testid={`unblock-user-${row.email}`}
                                  >
                                    <UserCheck className="w-3.5 h-3.5 mr-1" />
                                    {t('Unblock')}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    disabled={busyId === row.id}
                                    onClick={() => setPendingAction({ kind: 'delete', user: row })}
                                    aria-label={t('Delete account')}
                                    data-testid={`delete-user-${row.email}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    {t('Delete')}
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-muted-foreground">
              {t('{count} accounts', { count: fmtNum(total) })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => loadUsers(page - 1, search)}
                data-testid="platform-users-prev"
              >
                {t('Previous')}
              </Button>
              <span className="text-muted-foreground">
                {t('Page {page} of {total}', { page, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => loadUsers(page + 1, search)}
                data-testid="platform-users-next"
              >
                {t('Next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Block dialog carries an optional reason, so it's a custom Dialog
          rather than the plain ConfirmDialog. */}
      <Dialog
        open={pendingAction?.kind === 'block'}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAction(null);
            setBlockReason('');
          }
        }}
      >
        <DialogContent data-testid="block-user-dialog">
          <DialogHeader>
            <DialogTitle>{t('Block this account?')}</DialogTitle>
            <DialogDescription>
              {t(
                'The user loses access immediately — every session is rejected until you unblock them. Their campaigns and tracking links keep serving traffic.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="block-reason">{t('Reason (optional)')}</Label>
            <Textarea
              id="block-reason"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder={t('Visible to admins only')}
              rows={2}
              data-testid="block-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingAction(null);
                setBlockReason('');
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={Boolean(busyId)}
              onClick={confirmPending}
              data-testid="block-user-confirm"
            >
              {busyId ? t('Please wait…') : t('Block account')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingAction?.kind === 'unblock'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Unblock this account?')}
        description={t('The user will be able to sign in again immediately.')}
        confirmLabel={t('Unblock')}
        variant="default"
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />

      <ConfirmDialog
        open={pendingAction?.kind === 'suspendTraffic'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Suspend all campaign traffic for this account?')}
        description={t(
          'Public tracking links will return the same inactive-campaign response. Campaign settings stay unchanged, and traffic can be resumed instantly.'
        )}
        confirmLabel={t('Suspend traffic')}
        variant="destructive"
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />

      <ConfirmDialog
        open={pendingAction?.kind === 'resumeTraffic'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Resume campaign traffic for this account?')}
        description={t('Existing active campaigns and tracking links will start serving again.')}
        confirmLabel={t('Resume traffic')}
        variant="default"
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />

      <ConfirmDialog
        open={pendingAction?.kind === 'grant'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Grant platform admin?')}
        description={t(
          'This user gets operator access: the platform back-office, every account, and platform settings. The grant is audit-logged.'
        )}
        confirmLabel={t('Make admin')}
        variant="default"
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />

      <ConfirmDialog
        open={pendingAction?.kind === 'revoke'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Revoke platform admin?')}
        description={t('The user keeps their account but loses operator access.')}
        confirmLabel={t('Revoke admin')}
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />

      {/* Plan override dialog — Select + confirm, so it's a custom Dialog. */}
      <Dialog
        open={pendingAction?.kind === 'plan'}
        onOpenChange={(open) => !open && setPendingAction(null)}
      >
        <DialogContent data-testid="set-plan-dialog">
          <DialogHeader>
            <DialogTitle>{t('Change plan')}</DialogTitle>
            <DialogDescription>
              {t(
                'Sets the edition directly (status "active", no trial) — for manual and comp accounts. Accounts with a live Stripe subscription must change plans through Stripe.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t('Plan')}</Label>
            <Select value={planChoice} onValueChange={setPlanChoice}>
              <SelectTrigger data-testid="plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_ORDER.map((p) => (
                  <SelectItem key={p} value={p}>
                    {planLabel(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>
              {t('Cancel')}
            </Button>
            <Button
              disabled={Boolean(busyId)}
              onClick={confirmPending}
              data-testid="set-plan-confirm"
            >
              {busyId ? t('Please wait…') : t('Apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingAction?.kind === 'delete'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={t('Delete this account permanently?')}
        description={t(
          'This wipes the account and ALL of its data — campaigns, clicks, conversions, offers, landings, domains, reports. It cannot be undone.'
        )}
        confirmLabel={t('Delete account')}
        loading={Boolean(busyId)}
        onConfirm={confirmPending}
      />
    </div>
  );
}
